import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Play, Pause, Square, Upload, Volume2 } from 'lucide-react';
import { Button } from './ui/button';
import { useLibraryStore } from '../../stores/libraryStore';
import { useNotesStore } from '../../stores/notesStore';
import { useUserStore } from '../../stores/userStore';
import { aiApi } from '../../services/api';
import { UpgradeModal, useUpgradeCheck, LimitType } from './upgrade-modal';
import { useI18n, type TranslateFn } from '../../i18n';
import { toast } from 'sonner';
import WebViewer, { WebViewerInstance } from '@pdftron/webviewer';
import { alignSpeechWordsToPageText, findActiveWordIndex, tryPlayAudio, resolveArabicSpeechSelection, type ReadAlongRange } from './read-along.js';
import { alignOfficeSpeechWordsToPageText } from './office-text-order.js';
import { alignPdfSpeechWordsToPageText } from './pdf-text-order.js';
import { buildScannedReaderTranscript, type ReaderTranscriptSegment } from './scanned-reader-transcript.js';
import { readerAudioApi, ReaderAudioError, assertReaderRecording, type ReaderSpeechMark } from '../../services/reader-audio-api';
import { getServerSpeechPlaybackRate } from './read-along-playback-policy.js';
import { PdfSelectionToolbar } from './pdf-selection-toolbar';
import { getPdfPageScrollTop, getVisiblePdfPage } from './pdf-visible-page';
import { usePdfDocument } from './use-pdf-document';
import { PdfLibrarySave } from './pdf-library-save';
import pdfViewerPopupCss from './pdf-viewer-popup.css?url';

const READ_ALONG_PLAYBACK_RATE = 0.85; // Existing explanation playback remains unchanged.

function supportsDeviceSpeech() {
  return typeof window !== 'undefined'
    && 'speechSynthesis' in window
    && typeof SpeechSynthesisUtterance !== 'undefined';
}

type ReadAlongPlaybackRange = {
  audioSha256: string;
  startTimeMs: number;
  endTimeMs: number | null;
  startWordIndex: number;
  endWordIndexExclusive: number;
};

type AudioStatusMessage = {
  key: string;
  values?: Record<string, string | number>;
} | null;

type TranscriptViewport = {
  document: object;
  page: number;
  scrollTop: number;
  scrollLeft: number;
  pageOffset?: number;
};

function setReaderScrollTop(scroll: HTMLElement, targetTop: number) {
  const missingRoom = targetTop - (scroll.scrollHeight - scroll.clientHeight);
  if (missingRoom > 0 && missingRoom < scroll.clientHeight) {
    const padding = parseFloat(window.getComputedStyle(scroll).paddingBottom) || 0;
    scroll.style.paddingBottom = `${padding + Math.ceil(missingRoom) + 1}px`;
  }
  scroll.scrollTop = targetTop;
}

type LanguageViewSnapshot = {
  instance: WebViewerInstance;
  document: object;
  materialId: string | null;
  page: number;
  zoom: number;
  fitMode: ReturnType<WebViewerInstance['Core']['documentViewer']['getFitMode']>;
  scrollView: Element;
  top: number;
  left: number;
};

export function PDFViewerContainer() {
  const { t: translate, lang } = useI18n();
  // Audio events and in-flight requests must use the current interface language
  // without restarting playback, document loading, or status polling.
  const translateRef = useRef(translate);
  translateRef.current = translate;
  const t = useCallback<TranslateFn>((key, values) => translateRef.current(key, values), []);
  const { getToken, userId } = useAuth();
  const ownerRef = useRef(userId);
  ownerRef.current = userId;
  const { selectedMaterial, materials = [] } = useLibraryStore();
  const { saveNote } = useNotesStore();
  const { fetchLimits } = useUserStore();
  const { isAtLimit } = useUpgradeCheck();
  
  const viewerRef = useRef<HTMLDivElement>(null);
  const instanceRef = useRef<WebViewerInstance | null>(null);
  const initializedRef = useRef(false);
  const loadingSourceRef = useRef<{ id: string; owner: string | null | undefined } | null>(null);
  const loadedSourceRef = useRef<{ id: string; owner: string | null | undefined; document: object } | null>(null);
  const mountedRef = useRef(true);
  const languageUpdateRef = useRef<Promise<void>>(Promise.resolve());
  const languageViewRef = useRef<LanguageViewSnapshot | null>(null);
  const cancelLanguageView = useCallback(() => {
    const view = languageViewRef.current;
    languageViewRef.current = null;
    try {
      if (view && instanceRef.current === view.instance && mountedRef.current
        && selectedMaterialIdRef.current === view.materialId
        && view.instance.Core.documentViewer.getDocument() === view.document) {
        const page = view.instance.Core.documentViewer.getCurrentPage();
        if (page !== view.page) {
          stopPlaybackRef.current();
          readerPageRef.current = page;
          setPageNumber(page);
        }
      }
    } catch {
      // A disposed viewer cannot supply a safe page for continued playback.
      if (mountedRef.current) {
        stopPlaybackRef.current();
      }
    }
  }, []);
  
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.0);
  const readerPageRef = useRef(1);
  const pageAnchorRef = useRef<{ document: object; page: number; viewport?: TranscriptViewport } | null>(null);
  const restorePageAnchorRef = useRef<() => void>(() => {});
  const scrollIntentRef = useRef(false);
  const readAlongTranscriptRef = useRef<HTMLDivElement | null>(null);
  const transcriptViewportAnchorRef = useRef<TranscriptViewport | null>(null);
  const playbackAbortRef = useRef<AbortController | null>(null);
  const [audioErrorKey, setAudioErrorKey] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isTTSLoading, setIsTTSLoading] = useState(false);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [upgradeLimitType, setUpgradeLimitType] = useState<LimitType>('explain');
  const [currentAudio, setCurrentAudio] = useState<HTMLAudioElement | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isViewerReady, setIsViewerReady] = useState(false);
  
  // Pre-recorded audio status
  const [audioReadyKey, setAudioReadyKey] = useState<string | null>(null);
  const [audioStatusMessage, setAudioStatusMessage] = useState<AudioStatusMessage>(null);
  const [audioNotAvailable, setAudioNotAvailable] = useState(false);
  const [audioGenerationFailed, setAudioGenerationFailed] = useState(false);
  const [audioOnDemand, setAudioOnDemand] = useState(false);
  
  // Refs for audio management
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioCleanupRef = useRef<(() => void) | null>(null);
  const speechUtteranceRef = useRef<SpeechSynthesisUtterance | null>(null);
  const isCancelledRef = useRef(false);
  const playbackRequestRef = useRef(0);
  const stopPlaybackRef = useRef<() => void>(() => {});
  const audioReadyKeyRef = useRef<string | null>(null);
  const selectedMaterialIdRef = useRef<string | null>(selectedMaterial?.id || null);

  const [readAlongWords, setReadAlongWords] = useState<ReaderSpeechMark[]>([]);
  const [showReadAlongTranscript, setShowReadAlongTranscript] = useState(false);
  const readAlongTranscriptOnlyRef = useRef(false);
  const [scannedTranscript, setScannedTranscript] = useState<ReaderTranscriptSegment[] | null | undefined>(undefined);
  const [activeWordIdx, setActiveWordIdx] = useState(-1);
  const activeWordRef = useRef(-1);
  const wordTrackingCleanupRef = useRef<(() => void) | null>(null);
  const activeWordElRef = useRef<HTMLSpanElement | null>(null);
  const readAlongRangesRef = useRef<Array<ReadAlongRange | null>>([]);
  const readAlongPageRef = useRef<number | null>(null);
  const readAlongHighlightRef = useRef<any>(null);
  const highlightRequestRef = useRef(0);
  const playSelectedTextRef = useRef<(selectedText: string, page: number) => Promise<void>>(async () => {});
  const currentAudioKey = selectedMaterial ? `${selectedMaterial.id}:${pageNumber}` : null;
  const audioReady = audioReadyKey === currentAudioKey;
  selectedMaterialIdRef.current = selectedMaterial?.id || null;
  
  // Mobile audio unlock - AudioContext AND HTMLAudioElement must be primed during user gesture
  const audioContextRef = useRef<AudioContext | null>(null);
  const audioUnlockedRef = useRef(false);
  const warmAudioRef = useRef<HTMLAudioElement | null>(null);

  // A valid silent recording primes media during the user gesture.
  const SILENT_WAV = 'data:audio/wav;base64,UklGRkQDAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YSADAACAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgA==';
  
  // Unlock audio for mobile browsers - MUST be called synchronously in click handler
  const unlockAudio = useCallback(() => {
    try {
      // Create AudioContext if needed
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      }
      
      // Resume if suspended (required for mobile)
      if (audioContextRef.current.state === 'suspended') {
        audioContextRef.current.resume();
      }
      
      if (!audioUnlockedRef.current) {
        // Create and play a tiny silent buffer to fully unlock Web Audio API
        const buffer = audioContextRef.current.createBuffer(1, 1, 22050);
        const source = audioContextRef.current.createBufferSource();
        source.buffer = buffer;
        source.connect(audioContextRef.current.destination);
        source.start(0);
        audioUnlockedRef.current = true;
      }

      // Prime a reusable HTMLAudioElement during user gesture so .play() is
      // permitted later even after async gaps (mobile Safari / Chrome policy).
      if (!warmAudioRef.current) {
        warmAudioRef.current = new Audio();
      }
      const warm = warmAudioRef.current;
      warm.src = SILENT_WAV;
      warm.play().then(() => {
        if (warmAudioRef.current === warm) warm.pause();
      }).catch(() => {});
    } catch (e) {
      console.warn('[TTS] Audio unlock failed:', e);
    }
  }, []);

  useEffect(() => {
    audioReadyKeyRef.current = audioReadyKey;
  }, [audioReadyKey]);

  const stopWarmAudio = useCallback(() => {
    const warm = warmAudioRef.current;
    warmAudioRef.current = null;
    if (warm && warm !== audioRef.current) { warm.pause(); warm.removeAttribute('src'); }
  }, []);

  const beginPageRequest = useCallback((materialId: string, page: number) => {
    playbackAbortRef.current?.abort();
    const abort = new AbortController();
    playbackAbortRef.current = abort;
    const requestId = ++playbackRequestRef.current;
    const owner = ownerRef.current;
    const document = instanceRef.current?.Core.documentViewer.getDocument();
    isCancelledRef.current = false;
    return {
      signal: abort.signal,
      isCurrent: () => mountedRef.current && !abort.signal.aborted && !isCancelledRef.current
        && requestId === playbackRequestRef.current && ownerRef.current === owner
        && selectedMaterialIdRef.current === materialId && readerPageRef.current === page
        && instanceRef.current?.Core.documentViewer.getDocument() === document,
    };
  }, []);

  const reportReaderError = useCallback((error: any, notify = true) => {
    const code = error?.data?.code;
    if (['PAGE_AUDIO_PREPARATION_TIMEOUT', 'ENGLISH_PAGE_NARRATION_PENDING'].includes(code)) {
      audioReadyKeyRef.current = null;
      setAudioReadyKey(null);
      setAudioGenerationFailed(false);
      setAudioOnDemand(true);
      setAudioErrorKey(null);
      setAudioStatusMessage({ key: 'reader.audio.stillPreparing' });
      if (notify) toast.info(t('reader.audio.stillPreparing'));
      return;
    }
    const key = code === 'PAGE_AUDIO_ALIGNMENT_FAILED' || code === 'PAGE_AUDIO_IDENTITY_MISMATCH'
      || ['SELECTION_ALIGNMENT_FAILED', 'SELECTION_NOT_FOUND', 'READER_AUDIO_ARTIFACT_INVALID', 'READER_AUDIO_STALE'].includes(code) ? 'reader.audio.alignmentFailed'
      : ['READER_AUDIO_DISABLED', 'READER_AUDIO_GENERATION_DISABLED'].includes(code) ? 'reader.audio.unavailable'
      : code?.startsWith('READER_TEXT_') ? 'reader.audio.scanUnavailable'
      : ['PAGE_AUDIO_TEXT_ACCURACY_UNAVAILABLE', 'PAGE_AUDIO_SOURCE_UNAVAILABLE', 'PAGE_TEXT_UNAVAILABLE', 'READER_AUDIO_EMPTY'].includes(code)
        ? 'reader.audio.textUnavailable' : 'reader.audio.readFailed';
    audioReadyKeyRef.current = null;
    setAudioReadyKey(null);
    setAudioGenerationFailed(true);
    setAudioOnDemand(false);
    setAudioErrorKey(key);
    setAudioStatusMessage({ key: 'reader.audio.couldNotPrepare' });
    if (error?.status === 403) {
      setAudioStatusMessage({ key: 'reader.audio.outsideAllowance' });
      if (notify) { setUpgradeLimitType('book_audio'); setUpgradeModalOpen(true); }
    } else if (notify) toast.error(t(key));
  }, [t]);

  useEffect(() => {
    stopPlaybackRef.current();
  }, [selectedMaterial?.id, userId]);

  // Status never generates recordings, and a late check cannot replace a manual attempt.
  useEffect(() => {
    const abort = new AbortController();
    const owner = userId;
    let timer: number | null = null;
    const started = Date.now();
    const current = () => !abort.signal.aborted && mountedRef.current && ownerRef.current === owner;
    const check = async () => {
      if (!selectedMaterial || !current()) return;
      const attempt = playbackRequestRef.current;
      const sameAttempt = () => current() && attempt === playbackRequestRef.current;
      try {
        const token = await getToken();
        if (!token || !sameAttempt()) return;
        const result = await readerAudioApi.getStatus(selectedMaterial.id, token, pageNumber, abort.signal);
        if (!sameAttempt()) return;
        if (result.page?.status === 'error' || result.job?.status === 'failed') {
          reportReaderError(new ReaderAudioError(422, {
            code: result.page?.errorCode || 'PAGE_AUDIO_GENERATION_FAILED',
            retryable: result.page?.retryable,
          }), false);
          return;
        }
        const ready = result.page?.isReady === true;
        const missing = !result.page || result.page.status === 'missing';
        const key = `${selectedMaterial.id}:${pageNumber}`;
        audioReadyKeyRef.current = ready ? key : null;
        setAudioReadyKey(ready ? key : null);
        setAudioGenerationFailed(false);
        setAudioOnDemand(missing);
        setAudioStatusMessage(ready ? { key: 'reader.audio.readyPage', values: { page: pageNumber } }
          : { key: missing ? 'reader.audio.willPrepare' : 'reader.audio.generatingTitle' });
        if (ready || missing) return;
      } catch (error: any) {
        if (!sameAttempt()) return;
        if (error?.status === 403 || error?.status === 410 || error?.status === 503 || error?.status === 422) {
          reportReaderError(error, false);
          return;
        }
      }
      if (current() && Date.now() - started < 60000) timer = window.setTimeout(() => void check(), 3000);
      else if (current()) { setAudioOnDemand(true); setAudioStatusMessage({ key: 'reader.audio.willPrepare' }); }
    };
    audioReadyKeyRef.current = null;
    setAudioReadyKey(null);
    setAudioNotAvailable(false);
    setAudioGenerationFailed(false);
    setAudioOnDemand(false);
    setAudioErrorKey(null);
    setAudioStatusMessage(selectedMaterial ? { key: 'reader.audio.checkingStatus' } : null);
    void check();
    return () => { abort.abort(); if (timer !== null) window.clearTimeout(timer); };
  }, [selectedMaterial?.id, pageNumber, getToken, userId, reportReaderError]);

  // Every recording owns its media listeners, including an early stop or source replacement.
  const prepareAudioElement = useCallback((src: string, onCleanup?: () => void) => {
    if ('speechSynthesis' in window) { speechUtteranceRef.current = null; window.speechSynthesis.cancel(); }
    audioRef.current?.pause();
    audioCleanupRef.current?.();
    const audio = warmAudioRef.current || new Audio();
    warmAudioRef.current = null;
    audio.pause();
    audio.src = src;
    audio.currentTime = 0;
    audioRef.current = audio;
    let released = false;
    const release = () => {
      if (released) return;
      released = true;
      audio.removeEventListener('ended', ended);
      audio.removeEventListener('error', failed);
      onCleanup?.();
    };
    const ended = () => {
      if (audioRef.current === audio) {
        setIsPlaying(false); setIsPaused(false); setCurrentAudio(null); audioRef.current = null;
      }
      if (audioCleanupRef.current === release) audioCleanupRef.current = null;
      release();
    };
    const failed = () => {
      if (audioRef.current === audio) toast.error(t('reader.audio.playbackError'));
      ended();
    };
    audioCleanupRef.current = release;
    audio.addEventListener('ended', ended);
    audio.addEventListener('error', failed);
    return audio;
  }, [t]);

  const clearPdfReadAlongHighlight = useCallback(() => {
    highlightRequestRef.current += 1;
    const annotation = readAlongHighlightRef.current;
    const instance = instanceRef.current;
    readAlongHighlightRef.current = null;
    if (!annotation || !instance) return;
    try {
      instance.Core.annotationManager.deleteAnnotation(annotation, {
        imported: true,
        force: true,
        source: 'read-along',
      });
    } catch {
      return;
    }
  }, []);

  const captureTranscriptViewport = useCallback(() => {
    if (transcriptViewportAnchorRef.current) return;
    const viewer = instanceRef.current?.Core.documentViewer;
    const document = viewer?.getDocument();
    if (!viewer || !document) return;
    const scroll = viewer.getScrollViewElement();
    const page = readerPageRef.current;
    const origin = getPdfPageScrollTop(viewer, page);
    const zoom = viewer.getZoomLevel();
    const viewport: TranscriptViewport = {
      document, page, scrollTop: scroll.scrollTop, scrollLeft: scroll.scrollLeft,
      pageOffset: origin !== null && zoom > 0 ? (scroll.scrollTop - origin) / zoom : undefined,
    };
    transcriptViewportAnchorRef.current = viewport;
    pageAnchorRef.current = { document, page, viewport };
    scrollIntentRef.current = false;
  }, []);

  const resetReadAlongState = useCallback(() => {
    wordTrackingCleanupRef.current?.();
    wordTrackingCleanupRef.current = null;
    readAlongRangesRef.current = [];
    readAlongPageRef.current = null;
    clearPdfReadAlongHighlight();
    if (readAlongTranscriptRef.current) captureTranscriptViewport();
    setReadAlongWords([]);
    readAlongTranscriptOnlyRef.current = false;
    setShowReadAlongTranscript(false);
    setScannedTranscript(undefined);
    setActiveWordIdx(-1);
    activeWordRef.current = -1;
  }, [clearPdfReadAlongHighlight, captureTranscriptViewport]);

  const showPdfReadAlongHighlight = useCallback(async (
    page: number,
    range: ReadAlongRange | null
  ) => {
    if (!range || readAlongTranscriptOnlyRef.current) {
      clearPdfReadAlongHighlight();
      return;
    }

    const instance = instanceRef.current;
    if (!instance || readerPageRef.current !== page) {
      clearPdfReadAlongHighlight();
      return;
    }

    const requestId = ++highlightRequestRef.current;
    let sourceDocument: object | null = null;
    const useTranscriptFallback = () => {
      if (requestId !== highlightRequestRef.current || instanceRef.current !== instance
        || readerPageRef.current !== page || readAlongPageRef.current !== page
        || !sourceDocument || instance.Core.documentViewer.getDocument() !== sourceDocument) return;
      // Stay in one mode until the next playback; never resize on ordinary word gaps.
      captureTranscriptViewport();
      clearPdfReadAlongHighlight();
      readAlongTranscriptOnlyRef.current = true;
      setShowReadAlongTranscript(true);
      setAudioStatusMessage({ key: 'reader.audio.highlightsUnavailable' });
    };
    try {
      const { documentViewer, annotationManager, Annotations } = instance.Core;
      const document = documentViewer.getDocument();
      if (!document) return;
      sourceDocument = document;
      const segments = range.segments || [range];
      const segmentQuads = await Promise.all(segments.map(segment =>
        document.getTextPosition(page, segment.start, segment.end)
      ));
      if (segmentQuads.some(quads => !quads?.length)) {
        useTranscriptFallback();
        return;
      }
      const quads = segmentQuads.flat();
      if (requestId !== highlightRequestRef.current || !quads?.length
        || instanceRef.current !== instance || documentViewer.getDocument() !== document || readerPageRef.current !== page) return;

      const previous = readAlongHighlightRef.current;
      if (previous) {
        annotationManager.deleteAnnotation(previous, {
          imported: true,
          force: true,
          source: 'read-along',
        });
      }

      const highlight = new Annotations.TextHighlightAnnotation();
      highlight.PageNumber = page;
      highlight.Quads = quads as typeof highlight.Quads;
      highlight.StrokeColor = new Annotations.Color(255, 208, 0, 1);
      highlight.FillColor = new Annotations.Color(255, 208, 0, 0.45);
      highlight.Author = 'Sourcity';
      highlight.Subject = 'Read Along';
      highlight.Listable = false;
      highlight.Printable = false;
      highlight.NoMove = true;
      highlight.NoResize = true;
      readAlongHighlightRef.current = highlight;
      annotationManager.addAnnotation(highlight, {
        imported: true,
        source: 'read-along',
      });
      await annotationManager.drawAnnotationsFromList([highlight]);
    } catch {
      useTranscriptFallback();
    }
  }, [clearPdfReadAlongHighlight, captureTranscriptViewport]);

  const playPreRecordedAudio = useCallback(async (page: number, playbackRange?: ReadAlongPlaybackRange) => {
    if (!selectedMaterial) return;
    const materialId = selectedMaterial.id;
    const request = beginPageRequest(materialId, page);
    setIsTTSLoading(true);
    let playbackAudio: HTMLAudioElement | null = null;
    try {
      const token = await getToken();
      if (!request.isCurrent()) return;
      if (!token) throw new ReaderAudioError(401, { code: 'AUTHENTICATION_REQUIRED' });
      audioRef.current?.pause();
      audioCleanupRef.current?.();
      audioRef.current = null;
      setCurrentAudio(null);
      resetReadAlongState();
      const [audioData, marks] = await Promise.all([
        readerAudioApi.getPageAudioUrl(materialId, page, token, request.signal),
        readerAudioApi.getSpeechMarks(materialId, page, token, request.signal),
      ]);
      if (!request.isCurrent()) return;
      assertReaderRecording(audioData, marks, playbackRange?.audioSha256);
      const allWords = marks.marks.filter(mark => mark.type === 'word');
      const document = instanceRef.current?.Core.documentViewer.getDocument();
      let pageText = '';
      try { pageText = document ? await document.loadPageText(page) : ''; }
      catch { /* The verified recording can play even when PDF geometry is unavailable. */ }
      if (!request.isCurrent()) return;
      const scannedText = audioData.alignmentMode === 'ocr_transcript';
      const isOffice = /^(pptx?|docx?|xlsx?)$/iu.test(selectedMaterial.file_type || '');
      const allRanges = scannedText ? allWords.map(() => null) : isOffice && document
        ? await alignOfficeSpeechWordsToPageText({ document, pageNumber: page,
            pageText: pageText || '', words: allWords, language: audioData.language,
            signal: request.signal, isCurrent: request.isCurrent })
        : document && /^pdf$/iu.test(selectedMaterial.file_type || '')
          ? await alignPdfSpeechWordsToPageText({ document, pageNumber: page,
              pageText: pageText || '', words: allWords, language: audioData.language,
              signal: request.signal, isCurrent: request.isCurrent })
          : alignSpeechWordsToPageText(pageText || '', allWords);
      if (!request.isCurrent()) return;
      const hasHighlights = allRanges.some(Boolean);
      if (playbackRange && (!allWords.length || !hasHighlights)) {
        throw new ReaderAudioError(422, { code: 'SELECTION_ALIGNMENT_FAILED' });
      }
      const start = playbackRange?.startWordIndex ?? 0;
      const end = playbackRange?.endWordIndexExclusive ?? allWords.length;
      if (!Number.isInteger(start) || !Number.isInteger(end) || start < 0 || end > allWords.length || (playbackRange && end <= start)
        || (playbackRange && (!Number.isFinite(playbackRange.startTimeMs) || playbackRange.startTimeMs < 0
          || !Number.isFinite(playbackRange.endTimeMs) || playbackRange.endTimeMs! <= playbackRange.startTimeMs))) {
        throw new ReaderAudioError(422, { code: 'SELECTION_ALIGNMENT_FAILED' });
      }
      const audio = prepareAudioElement(audioData.audioUrl, resetReadAlongState);
      playbackAudio = audio;
      audio.playbackRate = getServerSpeechPlaybackRate(audioData.language);
      audio.preservesPitch = true;
      if ('webkitPreservesPitch' in audio) (audio as HTMLAudioElement & { webkitPreservesPitch: boolean }).webkitPreservesPitch = true;
      if (playbackRange) audio.currentTime = playbackRange.startTimeMs / 1000;
      const words = allWords.slice(start, end);
      readAlongRangesRef.current = allRanges.slice(start, end);
      readAlongPageRef.current = page;
      captureTranscriptViewport();
      readAlongTranscriptOnlyRef.current = !readAlongRangesRef.current.some(Boolean);
      setShowReadAlongTranscript(readAlongTranscriptOnlyRef.current);
      setReadAlongWords(words);
      setScannedTranscript(scannedText ? buildScannedReaderTranscript(marks.marks) : undefined);
      setActiveWordIdx(-1);
      activeWordRef.current = -1;
      let frame: number | null = null;
      const stopTracking = () => { if (frame !== null) window.cancelAnimationFrame(frame); frame = null; };
      const updateWord = () => {
        if (!request.isCurrent() || audioRef.current !== audio) return false;
        const time = audio.currentTime * 1000;
        if (playbackRange && time >= playbackRange.endTimeMs!) { stopPlaybackRef.current(); return false; }
        const word = findActiveWordIndex(words, time);
        if (word !== activeWordRef.current) { activeWordRef.current = word; setActiveWordIdx(word); }
        return true;
      };
      const track = () => {
        if (!updateWord()) return;
        frame = !audio.paused && !audio.ended ? window.requestAnimationFrame(track) : null;
      };
      const startTracking = () => { stopTracking(); track(); };
      wordTrackingCleanupRef.current = () => {
        stopTracking(); audio.removeEventListener('play', startTracking);
        audio.removeEventListener('pause', stopTracking); audio.removeEventListener('seeked', updateWord);
      };
      audio.addEventListener('play', startTracking);
      audio.addEventListener('pause', stopTracking);
      audio.addEventListener('seeked', updateWord);
      setCurrentAudio(audio);
      const result = await tryPlayAudio(audio);
      if (!request.isCurrent()) { audio.pause(); return; }
      if (result.status === 'error') throw result.error;
      setIsTTSLoading(false);
      setIsPlaying(result.status === 'playing');
      setIsPaused(result.status === 'blocked');
      if (result.status === 'blocked') {
        setAudioStatusMessage({ key: 'reader.audio.readyResume' });
        toast.info(t('reader.audio.readyResume'));
      } else if (!hasHighlights) {
        setAudioStatusMessage({ key: scannedText ? 'reader.audio.scanTranscript' : 'reader.audio.highlightsUnavailable' });
      }
      updateWord();
    } catch (error) {
      if (!request.isCurrent()) return;
      playbackAudio?.pause(); stopWarmAudio();
      audioCleanupRef.current?.(); audioCleanupRef.current = null;
      audioRef.current = null; setCurrentAudio(null);
      setIsTTSLoading(false); setIsPlaying(false); setIsPaused(false);
      resetReadAlongState(); reportReaderError(error);
    }
  }, [selectedMaterial, beginPageRequest, getToken, prepareAudioElement, resetReadAlongState,
    captureTranscriptViewport, stopWarmAudio, reportReaderError, t]);

  const ensurePageAudio = useCallback(async (documentId: string, page: number, token: string,
    request: ReturnType<typeof beginPageRequest>) => {
    const generated = await readerAudioApi.generatePageAudio(documentId, page, token, request.signal);
    if (!request.isCurrent()) throw new ReaderAudioError(499, { code: 'PLAYBACK_CANCELLED' });
    if (generated.status === 'ready') return;
    if (!['pending', 'processing', 'queued'].includes(generated.status)) {
      throw new ReaderAudioError(422, { code: 'PAGE_AUDIO_GENERATION_FAILED' });
    }
    // Long generative synthesis and timing are separate bounded operations.
    // Stop/page/owner changes still cancel immediately while preparation runs.
    let deadline = Date.now() + 300000;
    const maximumDeadline = Date.now() + 1800000;
    let completedPages = -1;
    const showTextProgress = (progress: typeof generated.textPreparation) => {
      if (!progress || !request.isCurrent()) return;
      if (Number.isSafeInteger(progress.completedPages) && progress.completedPages > completedPages) {
        completedPages = progress.completedPages;
        deadline = Math.min(maximumDeadline,Date.now() + 300000);
      }
      setAudioStatusMessage({key:'reader.audio.scanProgress',values:{completed:progress.completedPages,target:progress.targetPage}});
    };
    showTextProgress(generated.textPreparation);
    while (request.isCurrent() && Date.now() < deadline) {
      await new Promise<void>((resolve, reject) => {
        const onAbort = () => { window.clearTimeout(timer); reject(new ReaderAudioError(499, { code: 'PLAYBACK_CANCELLED' })); };
        const timer = window.setTimeout(() => { request.signal.removeEventListener('abort', onAbort); resolve(); }, 1000);
        request.signal.addEventListener('abort', onAbort, { once: true });
      });
      if (!request.isCurrent()) break;
      const freshToken = await getToken();
      if (!request.isCurrent()) break;
      if (!freshToken) throw new ReaderAudioError(401, { code: 'AUTHENTICATION_REQUIRED' });
      const status = await readerAudioApi.getStatus(documentId, freshToken, page, request.signal);
      if (!request.isCurrent()) break;
      showTextProgress(status.page?.textPreparation);
      if (!status.page?.textPreparation && completedPages >= 0) setAudioStatusMessage({key:'reader.audio.generatingTitle'});
      if (status.page?.status === 'ready' && status.page.isReady) return;
      if (status.page?.status === 'error' || status.job?.status === 'failed') {
        const code = status.page?.errorCode || 'PAGE_AUDIO_GENERATION_FAILED';
        throw new ReaderAudioError(['BOOK_AUDIO_LIMIT','READER_TEXT_PAGE_LIMIT','READER_TEXT_WORD_LIMIT'].includes(code) ? 403 : 422,
          {code,retryable:status.page?.retryable});
      }
    }
    throw new ReaderAudioError(408, { code: 'PAGE_AUDIO_PREPARATION_TIMEOUT', retryable: true });
  }, [getToken]);

  const speakWithDeviceVoice = useCallback((text: string, language: string) => {
    if (!mountedRef.current || !text.trim() || !supportsDeviceSpeech()) {
      return false;
    }

    const existingAudio = audioRef.current;
    if (existingAudio) {
      existingAudio.pause();
      audioRef.current = null;
      setCurrentAudio(null);
    }
    audioCleanupRef.current?.();
    audioCleanupRef.current = null;
    resetReadAlongState();
    speechUtteranceRef.current = null;
    window.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = language;
    utterance.rate = 0.95;
    utterance.onend = () => {
      if (speechUtteranceRef.current !== utterance) return;
      speechUtteranceRef.current = null;
      setIsPlaying(false);
      setIsPaused(false);
    };
    utterance.onerror = (event) => {
      if (speechUtteranceRef.current !== utterance) return;
      speechUtteranceRef.current = null;
      setIsPlaying(false);
      setIsPaused(false);
      if (event.error !== 'canceled') toast.error(t('reader.toast.speechFailed'));
    };

    speechUtteranceRef.current = utterance;
    setIsTTSLoading(false);
    setIsPlaying(true);
    setIsPaused(false);
    window.speechSynthesis.speak(utterance);
    return true;
  }, [resetReadAlongState, stopWarmAudio]);

  const playServerExplanationSpeech = useCallback(async (
    speech: { audioBase64: string; mimeType: string; language: string } | undefined,
    explanation: string,
    fallbackLanguage: string,
    unavailableReason?: 'daily_limit'
  ) => {
    if (!mountedRef.current) return;
    if (!speech?.audioBase64) {
      if (unavailableReason === 'daily_limit') {
        toast.error(t('reader.toast.speechLimit'));
        return;
      }
      if (!speakWithDeviceVoice(explanation, fallbackLanguage)) {
        toast.error(t('reader.toast.speechUnavailable'));
      }
      return;
    }

    stopPlaybackRef.current();
    if (!mountedRef.current) return;
    isCancelledRef.current = false;
    setIsTTSLoading(true);

    try {
      const binary = window.atob(speech.audioBase64);
      const bytes = new Uint8Array(binary.length);
      for (let index = 0; index < binary.length; index++) {
        bytes[index] = binary.charCodeAt(index);
      }
      if (!mountedRef.current) return;

      const audioUrl = URL.createObjectURL(new Blob([bytes], {
        type: speech.mimeType || 'audio/mpeg',
      }));
      const audio = prepareAudioElement(audioUrl, () => URL.revokeObjectURL(audioUrl));
      setCurrentAudio(audio);
      setIsPlaying(true);
      setIsPaused(false);
      setIsTTSLoading(false);
      await audio.play();
    } catch (error) {
      console.error('Explanation speech playback error:', error);
      if (!mountedRef.current) return;
      stopPlaybackRef.current();
      if (!speakWithDeviceVoice(explanation, fallbackLanguage)) {
        toast.error(t('reader.toast.speechPlayFailed'));
      }
    }
  }, [prepareAudioElement, speakWithDeviceVoice]);

  // Play selected text with seek-to-word if audio is ready
  const playSelectedText = useCallback(async (selectedText: string, page: number) => {
    if (!selectedMaterial || isLoading || loadedSourceRef.current?.id !== selectedMaterial.id) return;
    stopPlaybackRef.current();
    const materialId = selectedMaterial.id;
    const request = beginPageRequest(materialId, page);
    if (!request.isCurrent()) return;
    unlockAudio(); setIsTTSLoading(true); setAudioErrorKey(null);
    try {
      const token = await getToken();
      if (!request.isCurrent()) return;
      if (!token) throw new ReaderAudioError(401, { code: 'AUTHENTICATION_REQUIRED' });
      if (audioReadyKeyRef.current !== `${materialId}:${page}`) await ensurePageAudio(materialId, page, token, request);
      if (!request.isCurrent()) return;
      let matchedText = selectedText;
      let selectionHash: string | null = null;
      if (/\p{Script_Extensions=Arabic}/u.test(selectedText)) {
        const marks = await readerAudioApi.getSpeechMarks(materialId, page, token, request.signal);
        if (!request.isCurrent()) return;
        if (!/^[a-f0-9]{64}$/.test(marks.audioSha256 || '')) throw new ReaderAudioError(409, { code: 'PAGE_AUDIO_IDENTITY_MISMATCH' });
        const resolved = resolveArabicSpeechSelection(selectedText, marks.marks.filter(mark => mark.type === 'word'));
        if (!resolved) throw new ReaderAudioError(422, { code: 'SELECTION_ALIGNMENT_FAILED' });
        matchedText = resolved;
        selectionHash = marks.audioSha256;
      }
      const position = await readerAudioApi.findWordPosition(materialId, page, matchedText, token, request.signal);
      if (!request.isCurrent()) return;
      if (!/^[a-f0-9]{64}$/.test(position.audioSha256 || '')) throw new ReaderAudioError(409, { code: 'PAGE_AUDIO_IDENTITY_MISMATCH' });
      if (selectionHash && selectionHash !== position.audioSha256) throw new ReaderAudioError(409, { code: 'PAGE_AUDIO_IDENTITY_MISMATCH' });
      audioReadyKeyRef.current = `${materialId}:${page}`;
      setAudioReadyKey(audioReadyKeyRef.current); setAudioOnDemand(false); setAudioGenerationFailed(false);
      setAudioStatusMessage({ key: 'reader.audio.readyPage', values: { page } });
      await playPreRecordedAudio(page, {
        audioSha256: position.audioSha256,
        startTimeMs: position.position.timeMs, endTimeMs: position.position.endTimeMs,
        startWordIndex: position.position.wordIndex, endWordIndexExclusive: position.position.endWordIndexExclusive,
      });
    } catch (error) {
      if (!request.isCurrent()) return;
      stopWarmAudio(); setIsTTSLoading(false); reportReaderError(error);
    }
  }, [selectedMaterial, beginPageRequest, unlockAudio, getToken, ensurePageAudio, playPreRecordedAudio, stopWarmAudio, reportReaderError, isLoading]);

  useEffect(() => {
    playSelectedTextRef.current = playSelectedText;
  }, [playSelectedText]);

  // Keep native page controls authoritative; use painted page geometry only for user scrolling.
  useEffect(() => {
    if (!viewerRef.current || initializedRef.current) return;
    initializedRef.current = true;
    // The SDK's dispose does not remove its host. Give each effect generation
    // its own mount so StrictMode or a late initialization cannot leave an orphan.
    const mount = window.document.createElement('div');
    mount.style.width = '100%';
    mount.style.height = '100%';
    viewerRef.current.appendChild(mount);
    let disposed = false;
    let removeListeners = () => {};
    WebViewer({ path: '/lib/webviewer', css: pdfViewerPopupCss,
      licenseKey: 'demo:1755790281048:6065177403000000005079ec861e294db4eab5210fd5f38bc4a398155e',
    }, mount).then(instance => {
      if (disposed) { instance.UI.dispose(); return; }
      instanceRef.current = instance;
      setIsViewerReady(true);
      const { documentViewer } = instance.Core;
      let frame: number | null = null;
      let anchorFrame: number | null = null;
      let anchorRun: object | null = null;
      let nativePageIntentUntil = 0;
      const languageGuarded = () => {
        const view = languageViewRef.current;
        return view?.instance === instance && view.document === documentViewer.getDocument()
          && view.materialId === selectedMaterialIdRef.current;
      };
      const updatePage = (page: number) => {
        if (page === readerPageRef.current) return;
        readerPageRef.current = page;
        stopPlaybackRef.current();
        setPageNumber(page);
      };
      const updateVisiblePage = () => {
        const visiblePage = getVisiblePdfPage(documentViewer);
        updatePage(visiblePage);
        if (documentViewer.getCurrentPage() !== visiblePage && typeof documentViewer.updateView === 'function') {
          documentViewer.updateView(undefined, visiblePage);
        }
      };
      const restoreAnchoredPage = () => {
        const anchor = pageAnchorRef.current;
        if (!anchor || anchorRun === anchor) return;
        if (anchorFrame !== null) window.cancelAnimationFrame(anchorFrame);
        anchorRun = anchor;
        const started = performance.now();
        let changedAt = started;
        let geometry = '';
        let attempts = 0;
        const finish = () => { anchorFrame = null; anchorRun = null; };
        const tick = () => {
          if (disposed || pageAnchorRef.current !== anchor || documentViewer.getDocument() !== anchor.document) { finish(); return; }
          // The established locale restoration owns the same view while active.
          if (languageGuarded()) { finish(); return; }
          const scroll = documentViewer.getScrollViewElement() as HTMLElement;
          let root = scroll;
          while (root.parentElement) root = root.parentElement;
          const transitioning = root.getAnimations?.({ subtree: true }).some(animation =>
            'transitionProperty' in animation && !['opacity', 'background-color'].includes(String(animation.transitionProperty))
            && (animation.playState === 'running' || animation.pending));
          const current = [scroll.clientWidth, scroll.clientHeight, scroll.scrollWidth, scroll.scrollHeight,
            scroll.scrollTop, scroll.scrollLeft, documentViewer.getZoomLevel(), documentViewer.getCurrentPage()].join(':');
          if (current !== geometry || transitioning) changedAt = performance.now();
          geometry = current;
          if (performance.now() - started > 2000 || attempts >= 3) {
            pageAnchorRef.current = null;
            updatePage(documentViewer.getCurrentPage());
            finish(); return;
          }
          if (performance.now() - changedAt >= 200) {
            const viewport = anchor.viewport;
            if (viewport) {
              delete anchor.viewport;
              const origin = getPdfPageScrollTop(documentViewer, anchor.page);
              const target = origin !== null && viewport.pageOffset !== undefined
                ? origin + viewport.pageOffset * documentViewer.getZoomLevel() : viewport.scrollTop;
              setReaderScrollTop(scroll, target);
              scroll.scrollLeft = viewport.scrollLeft;
              changedAt = performance.now();
              anchorFrame = window.requestAnimationFrame(tick);
              return;
            }
            if (documentViewer.getCurrentPage() === anchor.page
              && (typeof documentViewer.updateView !== 'function' || getVisiblePdfPage(documentViewer) === anchor.page)) { finish(); return; }
            // SDK resize is throttled by 200ms. Restore after layout settles,
            // then observe again instead of masking a different Core page.
            const top = scroll.scrollTop, left = scroll.scrollLeft;
            attempts += 1;
            const alreadyVisible = typeof documentViewer.updateView === 'function'
              && getVisiblePdfPage(documentViewer) === anchor.page;
            if (alreadyVisible) {
              // Correct the old Web Component's stale page number through the
              // public API without scrolling the already visible page again.
              documentViewer.updateView(undefined, anchor.page);
            } else documentViewer.setCurrentPage(anchor.page, false);
            if (!alreadyVisible && (documentViewer.getDisplayModeManager as typeof documentViewer.getDisplayModeManager | undefined)) {
              // The old SDK centers a short last page, then reports the previous
              // visible page. Explicit navigation places the chosen page first.
              const targetTop = getPdfPageScrollTop(documentViewer, anchor.page);
              if (targetTop !== null) setReaderScrollTop(scroll, targetTop);
            } else if (scroll.scrollTop !== top) scroll.scrollTop = top;
            if (scroll.scrollLeft !== left) scroll.scrollLeft = left;
            changedAt = performance.now();
          }
          anchorFrame = window.requestAnimationFrame(tick);
        };
        anchorFrame = window.requestAnimationFrame(tick);
      };
      restorePageAnchorRef.current = restoreAnchoredPage;
      const pageChanged = (page: number) => {
        if (languageGuarded()) return;
        if (performance.now() < nativePageIntentUntil && page !== readerPageRef.current) {
          nativePageIntentUntil = 0;
          updatePage(page);
          pageAnchorRef.current = { document: documentViewer.getDocument(), page };
          return;
        }
        const anchor = pageAnchorRef.current;
        if (anchor?.document === documentViewer.getDocument()) {
          if (page !== anchor.page) restoreAnchoredPage();
          return;
        }
        if (scrollIntentRef.current) updateVisiblePage();
        else updatePage(page);
      };
      const documentLoaded = () => {
        if (scrollView) scrollView.style.paddingBottom = originalScrollPaddingBottom;
        const source = loadingSourceRef.current;
        const document = documentViewer.getDocument();
        if (!source || source.id !== selectedMaterialIdRef.current || source.owner !== ownerRef.current || !document) return;
        const loadedSource = { ...source, document };
        loadedSourceRef.current = loadedSource;
        pageAnchorRef.current = null;
        scrollIntentRef.current = false;
        readerPageRef.current = 1;
        setNumPages(documentViewer.getPageCount()); setPageNumber(1);
        // Office files can reveal more pages after documentLoaded. Refresh only
        // this document's total; conversion completion must not reset the page.
        void document.getDocumentCompletePromise?.().then(() => {
          if (disposed || !mountedRef.current || instanceRef.current !== instance
            || loadedSourceRef.current !== loadedSource
            || source.id !== selectedMaterialIdRef.current || source.owner !== ownerRef.current
            || documentViewer.getDocument() !== document) return;
          setNumPages(documentViewer.getPageCount());
        }).catch(() => {
          // Keep the pages already available; the SDK owns conversion errors.
        });
      };
      const scrollView = documentViewer.getScrollViewElement?.() as HTMLElement | undefined;
      const originalScrollPaddingBottom = scrollView?.style.paddingBottom || '';
      const inputRoot = scrollView?.getRootNode();
      const onInput = (event: Event) => {
        if (inputRoot === window.document && !event.composedPath().includes(viewerRef.current!)) return;
        const path = event.composedPath();
        const nativePageControl = path.some(node => node instanceof HTMLElement
          && node.matches('.PageNavOverlay, .PageControlsWrapper, [data-element="pageNavFlyoutMenu"]'));
        const submittedPage = event.type === 'submit' || (event.type === 'click'
          && path.some(node => node instanceof HTMLElement && node.tagName === 'BUTTON'));
        // A committed native page choice gets the same bounded preservation as
        // our arrows. Merely focusing an input or resizing must not create one.
        nativePageIntentUntil = nativePageControl && submittedPage ? performance.now() + 500 : 0;
        const hadAnchor = pageAnchorRef.current !== null;
        pageAnchorRef.current = null;
        if (hadAnchor && !languageGuarded()) updatePage(documentViewer.getCurrentPage());
        // A native toolbar click remains a native page choice, not a scroll heuristic.
        scrollIntentRef.current = event.type === 'wheel' || event.type === 'touchmove'
          || (event.type === 'pointerdown' && event.composedPath().includes(scrollView!));
      };
      const onScroll = () => {
        if (!scrollIntentRef.current || languageGuarded() || frame !== null) return;
        frame = window.requestAnimationFrame(() => {
          frame = null;
          if (!disposed && scrollIntentRef.current && !languageGuarded()) updateVisiblePage();
        });
      };
      const zoomChanged = () => setScale(documentViewer.getZoomLevel());
      const inputEvents = ['pointerdown', 'touchmove', 'wheel', 'keydown', 'click', 'submit'] as const;
      inputEvents.forEach(event => inputRoot?.addEventListener(event, onInput, true));
      scrollView?.addEventListener('scroll', onScroll, { passive: true });
      documentViewer.addEventListener('pageNumberUpdated', pageChanged);
      documentViewer.addEventListener('documentLoaded', documentLoaded);
      documentViewer.addEventListener('zoomUpdated', zoomChanged);
      removeListeners = () => {
        if (restorePageAnchorRef.current === restoreAnchoredPage) restorePageAnchorRef.current = () => {};
        if (scrollView) scrollView.style.paddingBottom = originalScrollPaddingBottom;
        if (frame !== null) window.cancelAnimationFrame(frame);
        if (anchorFrame !== null) window.cancelAnimationFrame(anchorFrame);
        inputEvents.forEach(event => inputRoot?.removeEventListener(event, onInput, true));
        scrollView?.removeEventListener('scroll', onScroll);
        documentViewer.removeEventListener('pageNumberUpdated', pageChanged);
        documentViewer.removeEventListener('documentLoaded', documentLoaded);
        documentViewer.removeEventListener('zoomUpdated', zoomChanged);
      };
    }).catch(error => {
      if (disposed) return;
      console.error('WebViewer initialization error:', error);
      toast.error(t('reader.toast.initFailed'));
    });
    return () => {
      disposed = true; removeListeners(); mount.remove();
      if (instanceRef.current) { instanceRef.current.UI.dispose(); instanceRef.current = null; }
      initializedRef.current = false;
    };
  }, [t]);

  // Setup text selection popup with TTS and Brain buttons
  const setupTextSelectionPopup = useCallback((instance: WebViewerInstance) => {
    const { UI, documentViewer } = instance.Core ? { UI: instance.UI, documentViewer: instance.Core.documentViewer } : instance as any;
    
    try {
      const customPopupElements = new Set([
        'sourcityReadAloud',
        'sourcityExplain',
        'sourcityWenda',
        'sourcitySaveNote',
      ]);
      const popupItems = UI.textPopup.getItems() as Array<{ dataElement?: string }>;
      UI.textPopup.update(popupItems.filter(item => !customPopupElements.has(item.dataElement || '')));

      // Add TTS Button (Speaker emoji)
      UI.textPopup.add([
        {
          type: 'actionButton',
          dataElement: 'sourcityReadAloud',
          img: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9ImN1cnJlbnRDb2xvciIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiPjxwb2x5Z29uIHBvaW50cz0iMTEgNSA2IDkgMiA5IDIgMTUgNiAxNSAxMSAxOSAxMSA1Ij48L3BvbHlnb24+PHBhdGggZD0iTTE1LjU0IDguNDZhNSA1IDAgMCAxIDAgNy4wNyI+PC9wYXRoPjxwYXRoIGQ9Ik0xOS4wNyA0LjkzYTEwIDEwIDAgMCAxIDAgMTQuMTQiPjwvcGF0aD48L3N2Zz4=',
          title: t('reader.popup.readAloud'),
          onClick: async () => {
            const selectedText = documentViewer.getSelectedText();
            const page = readerPageRef.current;
            if (selectedText) {
              await playSelectedTextRef.current(selectedText, page);
            }
          },
        },
        {
          type: 'actionButton',
          dataElement: 'sourcityExplain',
          img: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0Ij48dGV4dCB4PSIxMiIgeT0iMTciIHRleHQtYW5jaG9yPSJtaWRkbGUiIGZvbnQtZmFtaWx5PSJBcmlhbCxzYW5zLXNlcmlmIiBmb250LXNpemU9IjE2IiBmb250LXdlaWdodD0iYm9sZCIgZmlsbD0iY3VycmVudENvbG9yIj5FPC90ZXh0Pjwvc3ZnPg==',
          title: t('reader.popup.explain'),
          onClick: async () => {
            unlockAudio();
            const freshToken = await getToken();
            if (!freshToken) {
              toast.error(t('reader.toast.signIn'));
              return;
            }
            const selectedText = documentViewer.getSelectedText();
            const page = documentViewer.getCurrentPage();
            if (selectedText) {
              await explainText(selectedText, page, freshToken);
            }
          },
        },
        {
          type: 'actionButton',
          dataElement: 'sourcityWenda',
          img: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0Ij48dGV4dCB4PSIxMiIgeT0iMTYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGZvbnQtZmFtaWx5PSJBcmlhbCxzYW5zLXNlcmlmIiBmb250LXNpemU9IjE2IiBmb250LXdlaWdodD0iYm9sZCIgZmlsbD0iY3VycmVudENvbG9yIj7YuTwvdGV4dD48L3N2Zz4=',
          title: t('reader.popup.wenda'),
          onClick: async () => {
            unlockAudio();
            const freshToken = await getToken();
            if (!freshToken) {
              toast.error(t('reader.toast.signIn'));
              return;
            }
            const selectedText = documentViewer.getSelectedText();
            const page = documentViewer.getCurrentPage();
            if (selectedText) {
              await explainTextArabic(selectedText, page, freshToken);
            }
          },
        },
        {
          type: 'actionButton',
          dataElement: 'sourcitySaveNote',
          img: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9ImN1cnJlbnRDb2xvciIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiPjxwYXRoIGQ9Ik0xOSAyMUg1YTIgMiAwIDAgMS0yLTJWNWEyIDIgMCAwIDEgMi0yaDExbDUgNXYxMWEyIDIgMCAwIDEtMiAyeiI+PC9wYXRoPjxwb2x5bGluZSBwb2ludHM9IjE3IDIxIDE3IDEzIDcgMTMgNyAyMSI+PC9wb2x5bGluZT48cG9seWxpbmUgcG9pbnRzPSI3IDMgNyA4IDEyIDgiPjwvcG9seWxpbmU+PC9zdmc+',
          title: t('reader.popup.saveToNotes'),
          onClick: async () => {
            const freshToken = await getToken();
            if (!freshToken) {
              toast.error(t('reader.toast.signIn'));
              return;
            }
            const selectedText = documentViewer.getSelectedText();
            const page = documentViewer.getCurrentPage();
            if (selectedText && selectedMaterial) {
              await saveNoteFromSelection(selectedText, page, freshToken);
            }
          },
        },
      ]);
    } catch (error) {
      console.error('Failed to setup text popup:', error);
    }
  }, [selectedMaterial, unlockAudio, getToken, t]);

  const onDocumentLoadingChange = useCallback((loading: boolean) => {
    if (loading) {
      loadedSourceRef.current = null;
      loadingSourceRef.current = null;
      setNumPages(null);
    }
    setIsLoading(loading);
  }, []);
  const documentAnnotations = usePdfDocument({
    instance: isViewerReady ? instanceRef.current : null,
    material: selectedMaterial,
    userId,
    getToken,
    readAlongHighlight: readAlongHighlightRef,
    onLoadingChange: onDocumentLoadingChange,
    onBeforeLoad: source => { loadingSourceRef.current = source; },
    onReady: setupTextSelectionPopup,
  });

  // The selection popup captures its labels at setup time, so rebuild it — and
  // retranslate WebViewer's own chrome — whenever the language changes.
  useLayoutEffect(() => {
    const instance = instanceRef.current;
    if (!instance || !isViewerReady) return;
    let cancelled = false;
    const isCurrent = () => !cancelled && instanceRef.current === instance;
    // Keep selection actions available while the SDK language resource loads.
    setupTextSelectionPopup(instance);
    // Serialize SDK language loads so a slower previous selection cannot
    // overwrite the latest language. Skip obsolete queued changes.
    languageUpdateRef.current = languageUpdateRef.current.then(async () => {
      if (!isCurrent()) return;
      const documentViewer = instance.Core.documentViewer;
      const document = documentViewer.getDocument();
      const scrollView = document ? documentViewer.getScrollViewElement() : null;
      const view: LanguageViewSnapshot | null = document && scrollView ? {
        instance, document, materialId: selectedMaterialIdRef.current,
        page: pageAnchorRef.current?.document === document ? pageAnchorRef.current.page : documentViewer.getCurrentPage(), zoom: documentViewer.getZoomLevel(),
        fitMode: documentViewer.getFitMode(), scrollView,
        top: scrollView.scrollTop, left: scrollView.scrollLeft,
      } : null;
      if (view) { languageViewRef.current = view; scrollIntentRef.current = false; }
      const canRestore = () => view !== null && languageViewRef.current === view
        && instanceRef.current === instance && mountedRef.current
        && selectedMaterialIdRef.current === view.materialId
        && documentViewer.getDocument() === view.document;
      // User input takes precedence over a language-only snapshot, including
      // native PDF page/zoom controls and touch or wheel navigation.
      const inputRoot = scrollView?.getRootNode();
      const inputEvents = ['pointerdown', 'touchmove', 'wheel', 'keydown'] as const;
      const onReaderInput = (event: Event) => {
        // The current loader can use a ShadowRoot instead of an iframe. Its
        // ownerDocument also contains the app's language buttons.
        if (inputRoot !== window.document || event.composedPath().includes(viewerRef.current!)) cancelLanguageView();
      };
      for (const event of inputEvents) inputRoot?.addEventListener(event, onReaderInput, true);
      let layoutRoot = scrollView;
      while (layoutRoot?.parentElement) layoutRoot = layoutRoot.parentElement;
      const waitForLayout = async () => {
        let previous = '';
        let stableFrames = 0;
        let lastChange = performance.now();
        const deadline = lastChange + 2000;
        // setLanguage resolves before the SDK's React layout/render pipeline.
        // SDK 10.12 also runs scrollViewUpdated at the end of container CSS
        // transitions, and throttles resize by 200ms. A few quiet frames alone
        // can therefore finish before its delayed page/fit recalculation.
        for (let frame = 0; frame < 240 && canRestore() && performance.now() < deadline; frame++) {
          await new Promise<void>(resolve => window.requestAnimationFrame(() => resolve()));
          if (!canRestore()) return;
          const bounds = view!.scrollView.getBoundingClientRect();
          const geometry = [bounds.width, bounds.height, view!.scrollView.scrollWidth,
            view!.scrollView.scrollHeight, documentViewer.getZoomLevel(), documentViewer.getCurrentPage(),
            view!.scrollView.scrollTop, view!.scrollView.scrollLeft].join(':');
          const transitioning = layoutRoot?.getAnimations?.({ subtree: true }).some(animation =>
            'transitionProperty' in animation
            && !['opacity', 'background-color'].includes(String(animation.transitionProperty))
            && (animation.playState === 'running' || animation.pending));
          const unchanged = geometry === previous && !transitioning;
          stableFrames = unchanged ? stableFrames + 1 : 0;
          if (!unchanged) lastChange = performance.now();
          previous = geometry;
          if (stableFrames >= 3 && performance.now() - lastChange >= 200) return;
        }
      };
      let manualZoomRequired = false;
      const restoreView = () => {
        if (!view || !canRestore()) return;
        const zoomChanged = Math.abs(documentViewer.getZoomLevel() - view.zoom) > 0.000001;
        const restoreFitMode = !manualZoomRequired && documentViewer.getFitMode() !== view.fitMode;
        if (documentViewer.getCurrentPage() !== view.page) documentViewer.setCurrentPage(view.page, false);
        if (zoomChanged) documentViewer.zoomTo(view.zoom);
        // zoomTo switches fit mode; retain the user's previous fitting choice.
        if (!manualZoomRequired && documentViewer.getFitMode() !== view.fitMode) documentViewer.setFitMode(view.fitMode);
        // On mixed-size pages the SDK can have a fit mode whose cached zoom is
        // still from the previous page. Preserve the actual view in that case;
        // this changes only this viewer to manual zoom, not account preferences.
        if (Math.abs(documentViewer.getZoomLevel() - view.zoom) > 0.000001) {
          manualZoomRequired = true;
          documentViewer.zoomTo(view.zoom);
        }
        if (zoomChanged || restoreFitMode || documentViewer.getCurrentPage() !== view.page) documentViewer.setCurrentPage(view.page, false);
        view.scrollView.scrollTop = view.top;
        view.scrollView.scrollLeft = view.left;
      };
      try {
        await instance.UI.setLanguage(lang);
      } catch (error) {
        if (isCurrent()) {
          console.warn('Could not change WebViewer language:', lang, error);
          toast.error(t('reader.toast.languageFailed'));
        }
      } finally {
        try {
          if (view) {
            await waitForLayout();
            restoreView();
            await waitForLayout();
            restoreView();
            await waitForLayout();
          }
        } catch (error) {
          // Reconcile any suppressed page event before releasing the guard.
          cancelLanguageView();
          throw error;
        } finally {
          const restoreAnchor = canRestore();
          if (languageViewRef.current === view) languageViewRef.current = null;
          if (view && restoreAnchor) {
            // A late Core page event may have been suppressed by the locale
            // guard. Resume bounded reconciliation after releasing that guard.
            if (pageAnchorRef.current?.document !== view.document || pageAnchorRef.current.page !== view.page) {
              pageAnchorRef.current = { document: view.document, page: view.page };
            }
            restorePageAnchorRef.current();
          }
          for (const event of inputEvents) inputRoot?.removeEventListener(event, onReaderInput, true);
        }
      }
      if (isCurrent()) setupTextSelectionPopup(instance);
    }).catch(error => {
      // A disposed viewer or a failed layout read must not poison later changes.
      if (isCurrent()) {
        console.warn('Could not preserve WebViewer position during language change:', error);
        toast.error(t('reader.toast.languageFailed'));
      }
    });
    return () => { cancelled = true; };
  }, [lang, isViewerReady, setupTextSelectionPopup, cancelLanguageView]);

  // Explain text function
  const explainText = async (text: string, page: number, token: string) => {
    // Proactive limit check
    if (isAtLimit('explain')) {
      setUpgradeLimitType('explain');
      setUpgradeModalOpen(true);
      return;
    }

    try {
      toast.info(t('reader.toast.generatingExplanation'));
      
      const response = await aiApi.explainSelection(text, token, 'en', true);

      const explanation = response.choices[0]?.message?.content;

      if (explanation) {
        // Save as note
        if (selectedMaterial) {
          await saveNote(
            {
              note_type: 'explanation',
              selected_text: text,
              note_content: explanation,
              page_number: page,
              document_id: selectedMaterial.id,
              document_title: selectedMaterial.filename,
            },
            token
          );
        }

        toast.success(t('reader.toast.explanationSaved'));
        // Refresh limits after usage
        fetchLimits(token);
        if (!mountedRef.current) return;
        await playServerExplanationSpeech(
          response.speech,
          explanation,
          'en-US',
          response.speechUnavailableReason
        );
      }
    } catch (error: any) {
      console.error('Explain error:', error);
      if (error?.message?.includes('limit') || error?.message?.includes('Free tier')) {
        setUpgradeLimitType('explain');
        setUpgradeModalOpen(true);
      } else {
        toast.error(t('reader.toast.explanationFailed'));
      }
    }
  };

  const explainTextArabic = async (text: string, page: number, token: string) => {
    if (isAtLimit('explain')) {
      setUpgradeLimitType('explain');
      setUpgradeModalOpen(true);
      return;
    }

    try {
      toast.info(t('reader.toast.wendaGenerating'));
      
      const response = await aiApi.explainSelection(text, token, 'ar', true);

      const explanation = response.choices[0]?.message?.content;

      if (explanation) {
        if (selectedMaterial) {
          await saveNote(
            {
              note_type: 'explanation',
              selected_text: text,
              note_content: explanation,
              page_number: page,
              document_id: selectedMaterial.id,
              document_title: selectedMaterial.filename,
            },
            token
          );
        }

        toast.success(t('reader.toast.wendaSaved'));
        fetchLimits(token);
        if (!mountedRef.current) return;
        await playServerExplanationSpeech(
          response.speech,
          explanation,
          'ar-SA',
          response.speechUnavailableReason
        );
      }
    } catch (error: any) {
      console.error('Wenda error:', error);
      if (error?.message?.includes('limit') || error?.message?.includes('Free tier')) {
        setUpgradeLimitType('explain');
        setUpgradeModalOpen(true);
      } else {
        toast.error(t('reader.toast.wendaFailed'));
      }
    }
  };

  // Save note from selection
  const saveNoteFromSelection = async (text: string, page: number, token: string) => {
    try {
      if (!selectedMaterial) return;
      
      await saveNote(
        {
          note_type: 'highlight',
          selected_text: text,
          note_content: text,
          page_number: page,
          document_id: selectedMaterial.id,
          document_title: selectedMaterial.filename,
        },
        token
      );
      
      toast.success(t('reader.toast.savedToNotes'));
    } catch (error) {
      console.error('Save note error:', error);
      toast.error(t('reader.toast.saveNoteFailed'));
    }
  };

  // Explicit navigation must not lose its target when two pages fit in the viewport.
  const goToPage = (page: number) => {
    cancelLanguageView();
    const viewer = instanceRef.current?.Core.documentViewer;
    const document = viewer?.getDocument();
    if (!viewer || !document || page < 1 || page > (numPages || 1)) return;
    stopPlaybackRef.current();
    scrollIntentRef.current = false;
    pageAnchorRef.current = { document, page };
    readerPageRef.current = page;
    setPageNumber(page);
    viewer.setCurrentPage(page, false);
  };
  const goToPrevPage = () => goToPage(readerPageRef.current - 1);
  const goToNextPage = () => goToPage(readerPageRef.current + 1);

  // Zoom controls
  const zoomIn = () => {
    cancelLanguageView();
    if (instanceRef.current) {
      const currentZoom = instanceRef.current.Core.documentViewer.getZoomLevel();
      const newZoom = Math.min(currentZoom + 0.25, 3);
      instanceRef.current.Core.documentViewer.zoomTo(newZoom);
      setScale(newZoom);
    }
  };

  const zoomOut = () => {
    cancelLanguageView();
    if (instanceRef.current) {
      const currentZoom = instanceRef.current.Core.documentViewer.getZoomLevel();
      const newZoom = Math.max(currentZoom - 0.25, 0.5);
      instanceRef.current.Core.documentViewer.zoomTo(newZoom);
      setScale(newZoom);
    }
  };

  // The current reader page includes explicit native navigation and ignores locale-only layout.
  const playPageTTS = async () => {
    if (isTTSLoading || isPlaying) return;
    // Resume the prepared element inside this gesture, before priming anything else.
    if (isPaused && currentAudio && currentAudio === audioRef.current) { resumeTTS(); return; }
    if (!instanceRef.current || !selectedMaterial || isLoading || loadedSourceRef.current?.id !== selectedMaterial.id) return;
    const materialId = selectedMaterial.id;
    const page = readerPageRef.current;
    setAudioErrorKey(null); setAudioGenerationFailed(false);
    unlockAudio();
    if (audioReadyKeyRef.current === `${materialId}:${page}`) { await playPreRecordedAudio(page); return; }
    const request = beginPageRequest(materialId, page);
    setIsTTSLoading(true);
    try {
      const token = await getToken();
      if (!request.isCurrent()) return;
      if (!token) throw new ReaderAudioError(401, { code: 'AUTHENTICATION_REQUIRED' });
      await ensurePageAudio(materialId, page, token, request);
      if (!request.isCurrent()) return;
      audioReadyKeyRef.current = `${materialId}:${page}`;
      setAudioReadyKey(audioReadyKeyRef.current); setAudioOnDemand(false);
      setAudioStatusMessage({ key: 'reader.audio.readyPage', values: { page } });
      await playPreRecordedAudio(page);
    } catch (error) {
      if (!request.isCurrent()) return;
      stopWarmAudio(); setIsTTSLoading(false); reportReaderError(error);
    }
  };

  // Pause TTS - keeps the queue and position
  const pauseTTS = () => {
    if (currentAudio && isPlaying) {
      currentAudio.pause();
      setIsPlaying(false);
      setIsPaused(true);
    } else if (speechUtteranceRef.current && window.speechSynthesis.speaking) {
      window.speechSynthesis.pause();
      setIsPlaying(false);
      setIsPaused(true);
    }
  };

  // A browser that blocked delayed playback receives a fresh user gesture here.
  const resumeTTS = async () => {
    if (currentAudio && currentAudio === audioRef.current && isPaused) {
      const audio = currentAudio;
      const requestId = playbackRequestRef.current;
      const result = await tryPlayAudio(audio);
      if (audioRef.current !== audio || requestId !== playbackRequestRef.current || !mountedRef.current) { audio.pause(); return; }
      if (result.status === 'playing') { setIsPlaying(true); setIsPaused(false); }
      else { setIsPlaying(false); setIsPaused(true); toast.error(t('reader.audio.resumeFailed')); }
    } else if (speechUtteranceRef.current && isPaused) {
      window.speechSynthesis.resume(); setIsPlaying(true); setIsPaused(false);
    }
  };

  // Stop TTS - clears all audio
  const stopTTS = useCallback(() => {
    isCancelledRef.current = true;
    playbackAbortRef.current?.abort();
    stopWarmAudio();
    playbackRequestRef.current += 1;
    setIsTTSLoading(false);
    
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      try {
        audio.currentTime = 0;
      } catch {}
    }
    audioRef.current = null;
    setCurrentAudio(null);
    audioCleanupRef.current?.();
    audioCleanupRef.current = null;

    if ('speechSynthesis' in window) {
      speechUtteranceRef.current = null;
      window.speechSynthesis.cancel();
    }

    resetReadAlongState();

    setIsPlaying(false);
    setIsPaused(false);
  }, [resetReadAlongState, stopWarmAudio]);

  useEffect(() => {
    stopPlaybackRef.current = stopTTS;
  }, [stopTTS]);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      isCancelledRef.current = true;
      playbackAbortRef.current?.abort();
      playbackRequestRef.current += 1;
      wordTrackingCleanupRef.current?.();
      wordTrackingCleanupRef.current = null;

      const audio = audioRef.current;
      audioRef.current = null;
      if (audio) audio.pause();
      audioCleanupRef.current?.();
      audioCleanupRef.current = null;

      speechUtteranceRef.current = null;
      if ('speechSynthesis' in window) window.speechSynthesis.cancel();

      const warmAudio = warmAudioRef.current;
      warmAudioRef.current = null;
      if (warmAudio) warmAudio.pause();

      const audioContext = audioContextRef.current;
      audioContextRef.current = null;
      if (audioContext && audioContext.state !== 'closed') {
        void audioContext.close().catch(() => {});
      }
    };
  }, []);


  useEffect(() => {
    const page = readAlongPageRef.current;
    if (page === null || page !== pageNumber || activeWordIdx < 0) { clearPdfReadAlongHighlight(); return; }
    void showPdfReadAlongHighlight(page, readAlongRangesRef.current[activeWordIdx] || null);
  }, [activeWordIdx, pageNumber, clearPdfReadAlongHighlight, showPdfReadAlongHighlight]);

  useLayoutEffect(() => {
    const anchor = transcriptViewportAnchorRef.current;
    transcriptViewportAnchorRef.current = null;
    const viewer = instanceRef.current?.Core.documentViewer;
    if (!anchor || !viewer || viewer.getDocument() !== anchor.document
      || anchor.page !== readerPageRef.current || pageAnchorRef.current?.viewport !== anchor
      || languageViewRef.current) return;
    // Fit-page zoom changes asynchronously with transcript height. Preserve a
    // PDF-relative position after that layout settles, not the old screen center.
    restorePageAnchorRef.current();
  }, [readAlongWords, showReadAlongTranscript]);

  useEffect(() => {
    const transcript = readAlongTranscriptRef.current;
    const word = activeWordElRef.current;
    if (!transcript || !word) return;
    const bounds = transcript.getBoundingClientRect();
    const active = word.getBoundingClientRect();
    if (active.top < bounds.top || active.bottom > bounds.bottom) transcript.scrollTop += active.top - bounds.top - transcript.clientHeight / 2;
  }, [activeWordIdx]);

  // Determine what overlay to show
  const showInitializing = !isViewerReady;
  const showEmptyState = isViewerReady && !selectedMaterial;
  const firstActiveTranscriptSegment = scannedTranscript?.findIndex(segment => segment.markIndices.includes(activeWordIdx)) ?? -1;

  return (
    <div className="flex h-full min-h-0 flex-col bg-gradient-to-br from-white to-gray-50 relative">
      <PdfLibrarySave instance={isViewerReady ? instanceRef.current : null} material={selectedMaterial}
        userId={userId} getToken={getToken} annotations={documentAnnotations} />
      {/* Toolbar - only show when document is selected and viewer is ready */}
      <div role="toolbar" aria-label={t('reader.title')} className={`flex shrink-0 flex-wrap items-center justify-between gap-1 border-b border-white/20 bg-white/60 backdrop-blur-xl px-2 sm:px-4 py-2 shadow-sm transition-opacity ${selectedMaterial && isViewerReady ? 'opacity-100' : 'opacity-0 pointer-events-none h-0 py-0 overflow-hidden'}`}>
        {/* Page Navigation */}
        <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={goToPrevPage}
            aria-label={t('reader.previousPage')}
            disabled={pageNumber <= 1}
            className="h-8 w-8 p-0 hover:bg-white/60 text-[#434343] disabled:opacity-30"
          >
            <ChevronLeft className="h-4 w-4 rtl-flip" />
          </Button>
          <span className="text-sm text-[#434343] text-center whitespace-nowrap">
            <span className="hidden sm:inline">{t('reader.pagePrefix')}</span>{pageNumber}<span className="hidden sm:inline">{t('reader.pageOf', { total: numPages || '--' })}</span><span className="sm:hidden">/{numPages || '--'}</span>
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={goToNextPage}
            aria-label={t('reader.nextPage')}
            disabled={pageNumber >= (numPages || 1)}
            className="h-8 w-8 p-0 hover:bg-white/60 text-[#434343] disabled:opacity-30"
          >
            <ChevronRight className="h-4 w-4 rtl-flip" />
          </Button>
        </div>

        {/* Zoom Controls */}
        <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={zoomOut}
            aria-label={t('reader.zoomOut')}
            className="h-8 w-8 p-0 hover:bg-white/60 text-[#434343]"
          >
            <ZoomOut className="h-4 w-4" />
          </Button>
          <span className="text-sm text-[#434343] w-12 text-center">
            {Math.round(scale * 100)}%
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={zoomIn}
            aria-label={t('reader.zoomIn')}
            className="h-8 w-8 p-0 hover:bg-white/60 text-[#434343]"
          >
            <ZoomIn className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {selectedMaterial && isViewerReady && instanceRef.current && (
        <PdfSelectionToolbar instance={instanceRef.current} disabled={isLoading || numPages === null} />
      )}
      {selectedMaterial && isViewerReady && (
        <div data-reader-audio-controls className="flex shrink-0 flex-col items-center border-b border-violet-100 bg-violet-50/60 px-2 py-1 sm:px-4">
          <Button size="sm" onClick={playPageTTS} disabled={isLoading || !loadedSourceRef.current || isTTSLoading || (isPlaying && !isPaused)}
            className="group h-11 w-[156.8px] max-w-full rounded-lg bg-transparent p-0 hover:bg-transparent disabled:opacity-100"
            title={audioReady ? t('reader.audio.playTitle') : t('reader.audio.prepareTitle')}
            aria-label={t(isTTSLoading ? 'reader.audio.loading' : isPaused ? 'reader.audio.resume' : 'reader.audio.readPage')}>
            <span data-reader-read-surface className="flex h-[33.6px] w-full items-center justify-center gap-1.5 rounded-lg bg-[#5B46C8] px-2 text-sm font-semibold text-white shadow-sm group-hover:bg-[#4936AA] group-disabled:bg-violet-100 group-disabled:text-violet-700">
              {isTTSLoading ? <span aria-hidden="true" className="size-4 shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent" /> : <Play aria-hidden="true" className="size-4" />}
              {t(isTTSLoading ? 'reader.audio.loading' : isPaused ? 'reader.audio.resume' : 'reader.audio.readPage')}
            </span>
          </Button>
          <div className="flex min-h-11 flex-wrap items-center justify-center gap-2">
            <Button variant="ghost" size="sm" onClick={pauseTTS} disabled={!isPlaying || isPaused}
              className="h-11 w-11 p-0 text-[#434343]" aria-label={t('reader.audio.pauseTitle')} title={t('reader.audio.pauseTitle')}>
              <Pause aria-hidden="true" className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={stopTTS} disabled={!isPlaying && !isPaused && !isTTSLoading}
              className="h-11 w-11 p-0 text-red-500 disabled:text-[#434343]" aria-label={t('reader.audio.stopTitle')} title={t('reader.audio.stopTitle')}>
              <Square aria-hidden="true" className="h-4 w-4" />
            </Button>
            {!audioNotAvailable && <div role="status" aria-live="polite" className="hidden items-center gap-1 rounded-full px-2 py-1 text-xs text-[#434343] sm:flex"
              title={audioStatusMessage ? t(audioStatusMessage.key, audioStatusMessage.values) : t(audioReady ? 'reader.audio.readyTitle' : 'reader.audio.generatingTitle')}>
              <Volume2 aria-hidden="true" className="h-3 w-3" />
              {t(audioOnDemand ? 'reader.audio.prepareOnPlay' : audioGenerationFailed ? 'reader.audio.failed' : audioReady ? 'reader.audio.ready' : 'reader.audio.generating')}
            </div>}
          </div>
          {audioErrorKey && !isTTSLoading && <p data-reader-audio-error role="alert" className="max-w-lg px-2 pb-1 text-center text-xs leading-relaxed text-red-700">{t(audioErrorKey)}</p>}
          {isTTSLoading && audioStatusMessage?.key === 'reader.audio.scanProgress' && (
            <p role="status" className="max-w-lg px-2 pb-1 text-center text-xs leading-relaxed text-[#434343]">
              {t('reader.audio.scanProgress',audioStatusMessage.values)}
            </p>
          )}
          {!isTTSLoading && audioStatusMessage?.key === 'reader.audio.stillPreparing' && (
            <p role="status" className="max-w-lg px-2 pb-1 text-center text-xs leading-relaxed text-[#434343]">
              {t('reader.audio.stillPreparing')}
            </p>
          )}
          {(isPlaying || isPaused) && ['reader.audio.highlightsUnavailable','reader.audio.scanTranscript'].includes(audioStatusMessage?.key || '') && (
            <p role="status" className="max-w-lg px-2 pb-1 text-center text-xs leading-relaxed text-[#434343]">
              {t(audioStatusMessage!.key)}
            </p>
          )}
        </div>
      )}

      {/* WebViewer Container */}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <div data-reader-viewport className="min-h-0 flex-1 relative overflow-hidden">
        {/* Initializing overlay */}
        {showInitializing && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/95 backdrop-blur-sm z-20">
            <div className="text-center p-8">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-[#7367FF]/10 flex items-center justify-center">
                <div className="animate-spin h-10 w-10 border-4 border-[#7367FF] border-t-transparent rounded-full"></div>
              </div>
              <h3 className="text-lg font-semibold text-[#434343] mb-2">{t('reader.initializingTitle')}</h3>
              <p className="text-[#434343]/60 text-sm">
                {selectedMaterial 
                  ? t('reader.preparingDocument', { name: selectedMaterial.original_filename || selectedMaterial.filename })
                  : t('reader.initializingBody')
                }
              </p>
            </div>
          </div>
        )}
        
        {/* Empty state overlay */}
        {showEmptyState && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/95 backdrop-blur-sm z-20">
            <div className="text-center p-8">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-[#7367FF]/10 flex items-center justify-center">
                <Upload className="h-10 w-10 text-[#7367FF]" />
              </div>
              <h3 className="text-lg font-semibold text-[#434343] mb-2">{t('reader.noDocumentTitle')}</h3>
              <p className="text-[#434343]/60 text-sm mb-2">
                {t('reader.noDocumentBody')}
              </p>
              {Array.isArray(materials) && materials.length === 0 && (
                <p className="text-xs text-[#434343]/40">
                  {t('reader.firstUploadBody')}
                </p>
              )}
            </div>
          </div>
        )}
        
        {selectedMaterial && documentAnnotations.status === 'load-error' && (
          <div role="alert" className="absolute inset-0 z-20 flex items-center justify-center bg-white/95 p-5">
            <div className="max-w-sm space-y-3 text-center">
              <p className="text-sm text-red-800">{t('reader.documentSave.loadFailed')}</p>
              <Button variant="outline" onClick={documentAnnotations.retryLoad}>{t('reader.documentSave.retryLoad')}</Button>
            </div>
          </div>
        )}
        {/* Loading document overlay */}
        {isLoading && !showInitializing && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/80 backdrop-blur-sm z-10">
            <div className="text-center">
              <div className="animate-spin h-8 w-8 border-4 border-[#7367FF] border-t-transparent rounded-full mx-auto mb-2"></div>
              <p className="text-[#434343]">{t('reader.loadingDocument')}</p>
            </div>
          </div>
        )}
        
        <div
          ref={viewerRef}
          style={{ height: '100%', width: '100%' }}
        />

      </div>
        {showReadAlongTranscript && readAlongWords.length > 0 && (
          <div ref={readAlongTranscriptRef} data-reader-transcript data-active-mark-index={scannedTranscript !== undefined ? activeWordIdx : undefined} className="max-h-[min(6rem,20%)] shrink-0 overflow-y-auto border-t border-[#7367FF]/20 bg-white px-3 py-2">
            <p className="mb-1 text-[10px] text-[#434343]/40">{t('reader.readAlong')}</p>
            <p dir="auto" className="text-start text-[15px] leading-relaxed text-[#434343]" style={scannedTranscript !== undefined ? { whiteSpace: 'pre-wrap' } : undefined}>
              {scannedTranscript === undefined ? readAlongWords.map((word, i) => (
                <span key={i} ref={i === activeWordIdx ? activeWordElRef : null}
                  className={i === activeWordIdx ? 'rounded bg-[#FFD000] px-0.5 text-black' : ''}>{word.value}{' '}</span>
              )) : scannedTranscript === null ? (
                <span ref={activeWordElRef} data-reader-transcript-fallback className="rounded bg-[#FFD000] px-0.5 text-black">{readAlongWords[activeWordIdx]?.value || ''}</span>
              ) : scannedTranscript.map((segment, i) => {
                const active = segment.markIndices.includes(activeWordIdx);
                return <React.Fragment key={`${segment.start}:${segment.end}`}>
                  {segment.gapBefore ? ' ' : null}
                  <span ref={i === firstActiveTranscriptSegment ? activeWordElRef : null} data-source-start={segment.start} data-source-end={segment.end}
                    data-reader-segment-active={active ? 'true' : 'false'}
                    className={active ? 'rounded bg-[#FFD000] text-black' : ''}>{segment.value}</span>
                </React.Fragment>;
              })}
            </p>
          </div>
        )}
      </div>

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={upgradeModalOpen}
        onClose={() => setUpgradeModalOpen(false)}
        limitType={upgradeLimitType}
      />
    </div>
  );
}
